import React, { useEffect, useState, useRef } from "react";
import { NavLink, useNavigate, Outlet } from "react-router-dom";
import { LogOut, WifiOff, Clock, Download, Menu, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useNetwork } from "@/context/NetworkContext";
import ThemeSwitch from "@/theme/ThemeSwitch";
import { Avatar, ThreeDotMenu, Modal, Button } from "@/ui";
import api from "@/lib/api";
import { manilaDateTime } from "@/lib/format";
import { navForRole, mobileBarForRole } from "@/nav/config";
import CommandBox from "@/components/CommandBox";

const ROLE_LABELS = { owner: "platform owner", partner: "partner", helper: "helper", shop_owner: "shop owner", staff: "staff" };

function useInstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [canShow, setCanShow] = useState(false);
  useEffect(() => {
    const visits = Number(localStorage.getItem("reto_visits") || "0") + 1;
    localStorage.setItem("reto_visits", String(visits));
    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
      // quiet button only from the third visit onward, never on first load
      if (visits >= 3 && localStorage.getItem("reto_installed") !== "1") setCanShow(true);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", () => { localStorage.setItem("reto_installed", "1"); setCanShow(false); });
    return () => window.removeEventListener("beforeinstallprompt", onPrompt);
  }, []);
  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    await deferred.userChoice;
    setCanShow(false);
  };
  return { canShow, install };
}

function Sidebar({ sections }) {
  return (
    <aside className="sidebar">
      <div className="brand">
        <span className="brand-mark">R</span>
        <div className="t-h3">Reto</div>
      </div>
      {sections.map((sec, i) => (
        <div key={i}>
          {sec.heading ? <div className="nav-group-heading t-label">{sec.heading}</div> : null}
          <div className="nav-group">
            {sec.items.map((it) => (
              <NavLink key={it.to} to={it.to} data-testid={`nav-${it.to.slice(1)}`}
                className={({ isActive }) => `nav-item${isActive ? " active" : ""}`}>
                {it.Icon ? <it.Icon className="icon" size={20} strokeWidth={1.75} aria-hidden="true" /> : null}
                <span>{it.label}</span>
              </NavLink>
            ))}
          </div>
        </div>
      ))}
    </aside>
  );
}

function MoreSheet({ open, onClose, sections }) {
  if (!open) return null;
  return (
    <div className="overlay" role="dialog" aria-modal="true" aria-label="More" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="drawer">
        <div className="row-between" style={{ marginBottom: 16 }}>
          <h2 className="t-h2">menu</h2>
          <button className="btn btn-ghost btn-sm" aria-label="Close" onClick={onClose}><X size={20} strokeWidth={1.75} /></button>
        </div>
        <div className="sheet-list">
          {sections.map((sec, i) => (
            <React.Fragment key={i}>
              {sec.heading ? <div className="sheet-group-heading t-label">{sec.heading}</div> : null}
              {sec.items.map((it) => (
                <NavLink key={it.to} to={it.to} className="sheet-item" onClick={onClose} data-testid={`more-${it.to.slice(1)}`}>
                  {it.Icon ? <it.Icon className="icon" size={20} strokeWidth={1.75} aria-hidden="true" /> : null}
                  <span>{it.label}</span>
                </NavLink>
              ))}
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function AppShell() {
  const { user, shopName, logout } = useAuth();
  const { online, queueCount } = useNetwork();
  const { canShow, install } = useInstallPrompt();
  const navigate = useNavigate();
  const [moreOpen, setMoreOpen] = useState(false);
  const [devOpen, setDevOpen] = useState(false);
  const [devices, setDevices] = useState([]);

  const sections = navForRole(user?.role);
  const bar = mobileBarForRole(user?.role);
  const isStaff = user?.role === "staff";
  const signOut = async () => { await logout(); navigate(isStaff ? "/counter-signin" : "/login", { replace: true }); };
  const openDevices = async () => {
    setDevOpen(true);
    try { const r = await api.get("/auth/sessions"); setDevices(r.data.devices); } catch { setDevices([]); }
  };
  const revokeDevice = async (id) => {
    try { await api.post(`/auth/sessions/${id}/revoke`, {}); const r = await api.get("/auth/sessions"); setDevices(r.data.devices); } catch {}
  };
  const menuItems = [
    { label: "Your devices", onClick: openDevices },
    { label: isStaff ? "End shift" : "Sign out", onClick: signOut },
  ];

  return (
    <div className="shell">
      <div className="app-bg" aria-hidden="true" />
      <div className="shell-layout">
        <Sidebar sections={sections} />
        <div className="main-col">
          <header className="topbar">
            <div className="row">
              <Avatar name={user?.name} />
              <div>
                <div className="t-h3" style={{ lineHeight: 1.2 }}>{shopName || user?.name}</div>
                <div className="muted-3 t-small">{ROLE_LABELS[user?.role] || user?.role}</div>
              </div>
            </div>
            <div className="row">
              <div className="status-chips">
                {!online ? <span className="chip warn" data-testid="offline-chip"><WifiOff size={16} strokeWidth={1.75} />offline</span> : null}
                {queueCount > 0 ? <span className="chip" data-testid="queue-chip"><Clock size={16} strokeWidth={1.75} />{queueCount} order{queueCount > 1 ? "s" : ""} waiting to send</span> : null}
              </div>
              {canShow ? <button className="btn btn-sm install-btn" data-testid="install-btn" onClick={install}><Download size={16} strokeWidth={1.75} />install app</button> : null}
              <ThemeSwitch />
              <div className="hide-mobile"><ThreeDotMenu items={menuItems} /></div>
              <button className="btn btn-ghost btn-sm" data-testid="sign-out" onClick={signOut} aria-label={isStaff ? "End shift" : "Sign out"}><LogOut size={20} strokeWidth={1.75} /></button>
            </div>
          </header>
          <main className="page">
            <Outlet />
          </main>
        </div>
      </div>
      {user?.role === "shop_owner" ? <CommandBox /> : null}

      <nav className="mnav">
        {bar.map((it, i) => (
          it.more ? (
            <button key={i} onClick={() => setMoreOpen(true)} data-testid="mnav-more" className={moreOpen ? "active" : ""}>
              <Menu size={20} strokeWidth={1.75} aria-hidden="true" /><span>{it.label}</span>
            </button>
          ) : (
            <NavLink key={i} to={it.to} data-testid={`mnav-${it.to.slice(1)}`} className={({ isActive }) => (isActive ? "active" : "")}>
              <it.Icon size={20} strokeWidth={1.75} aria-hidden="true" /><span>{it.label}</span>
            </NavLink>
          )
        ))}
      </nav>

      <MoreSheet open={moreOpen} onClose={() => setMoreOpen(false)} sections={sections} />

      <Modal open={devOpen} title="your devices" onClose={() => setDevOpen(false)}
        footer={<Button onClick={() => setDevOpen(false)}>done</Button>}>
        <p className="muted t-small" style={{ marginBottom: 12 }}>these devices are signed in. sign out any you don't recognise.</p>
        <div className="stack" data-testid="devices-list">
          {devices.length === 0 ? <p className="muted-3 t-small">no other devices.</p> : devices.map((d) => (
            <div key={d.id} className="row-between">
              <div>
                <div className="t-body">{d.current ? "this device" : (d.device || "a device").slice(0, 40)}{d.current ? " (now)" : ""}</div>
                <div className="muted-3 t-small">last used {manilaDateTime(d.lastSeenAt)}</div>
              </div>
              {!d.current ? <Button size="sm" variant="danger" data-testid={`revoke-${d.id}`} onClick={() => revokeDevice(d.id)}>sign out</Button> : null}
            </div>
          ))}
        </div>
      </Modal>
    </div>
  );
}
